// Copyright 2025, University of Maryland, All Rights Reserved
import { useState } from "react"
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  CHECK_LIST,
  ELEMENT_TRANSFORMERS,
  LINK,
  MULTILINE_ELEMENT_TRANSFORMERS,
  TEXT_FORMAT_TRANSFORMERS,
  TEXT_MATCH_TRANSFORMERS,
  type Transformer,
} from "@lexical/markdown"
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary"
import { ListPlugin } from "@lexical/react/LexicalListPlugin"
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin"
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin"

import { ContentEditable } from "@/components/editor/editor-ui/content-editable"
import { ActionsPlugin } from "@/components/editor/plugins/actions/actions-plugin"
import { MarkdownTogglePlugin } from "@/components/editor/plugins/actions/markdown-toggle-plugin"
import { ElementFormatToolbarPlugin } from "@/components/editor/plugins/toolbar/element-format-toolbar-plugin"
import { TextFormatToolbarPlugin } from "@/components/editor/plugins/toolbar/text-format-toolbar-plugin"
import { ToolbarPlugin } from "@/components/editor/plugins/toolbar/toolbar-plugin"
import { Separator } from "@/components/ui/separator"

// Markdown transformers for parsing and serializing
const TRANSFORMERS: Array<Transformer> = [
  CHECK_LIST,
  LINK,
  ...ELEMENT_TRANSFORMERS,
  ...MULTILINE_ELEMENT_TRANSFORMERS,
  ...TEXT_FORMAT_TRANSFORMERS,
  ...TEXT_MATCH_TRANSFORMERS,
]

export function Plugins({
  ariaLabel,
  toolbar = "full",
}: {
  ariaLabel?: string
  toolbar?: "full" | "minimal"
}) {
  const [_floatingAnchorElem, setFloatingAnchorElem] =
    useState<HTMLDivElement | null>(null)

  const onRef = (elem: HTMLDivElement) => {
    if (elem !== null) {
      setFloatingAnchorElem(elem)
    }
  }

  return (
    <div className="relative">
      {/* Toolbar plugins */}
      <ToolbarPlugin>
        {() => (
          <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2 flex-wrap">
            <TextFormatToolbarPlugin
              formats={toolbar === "minimal" ? ["bold"] : undefined}
              separator={toolbar !== "minimal"}
              showBlockTypes={toolbar !== "minimal"}
            />
            {toolbar === "full" && (
              <>
                <Separator orientation="vertical" className="!h-6" />
                <ElementFormatToolbarPlugin />
              </>
            )}
            <Separator orientation="vertical" className="!h-6" />
            <ActionsPlugin>
              <MarkdownTogglePlugin
                shouldPreserveNewLinesInMarkdown={false}
                transformers={TRANSFORMERS}
              />
            </ActionsPlugin>
          </div>
        )}
      </ToolbarPlugin>

      {/* Editor content */}
      <div className="relative bg-background">
        <RichTextPlugin
          contentEditable={
            <div className="min-h-[180px] max-h-[400px] overflow-y-auto">
              <div className="px-4 py-3" ref={onRef}>
                <ContentEditable
                  ariaLabel={ariaLabel}
                  placeholder="Start typing..."
                  className="ContentEditable__root relative block min-h-[160px] focus:outline-none prose prose-sm max-w-none prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground prose-strong:text-foreground prose-blockquote:text-foreground text-foreground"
                  placeholderClassName="text-muted-foreground/60 pointer-events-none absolute top-3 left-4 right-4 overflow-hidden text-ellipsis select-none"
                />
              </div>
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />

        {/* Editor plugins */}
        <ListPlugin />
        <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
      </div>
    </div>
  )
}

// Export transformers and conversion functions for external use
export { TRANSFORMERS, $convertFromMarkdownString, $convertToMarkdownString }
